import { MONTHS_GENITIVE, monthGrid, WEEKDAYS_SHORT, weekdayIndex } from '../events/calendar';
import { changeNote, type DayChange } from '../requests/types';
import type { TimelinePoint } from '../requests/timeline';
import { ASSIGNMENT_STATUSES, CREW_KEY, hoursText, type AssignmentStatus } from './types';
import type { MyAssignment } from './MyCalendarPage';
import type { PrivateTask } from '../private-tasks/types';

/**
 * „Mój kalendarz” (owner 2026-10-28 21:32): the month of the person's own days — their availability, their events and
 * the questions about each day — as pure functions the page and its tests share.
 */

/** One of my marked days as `/api/me/availability` sends it; `startsAt` / `endsAt`: only some hours of the day. */
export type MyMark = { day: string; state: 'available' | 'unavailable'; startsAt?: string; endsAt?: string };
/** `answer`: as it stands; `changed` (owner 2026-10-30 01:30): a newer mark of the day made it so (what I answered, how, when). */
export type MyQuestionDay = { day: string; answer: boolean | null; stale?: true; changed?: DayChange };
/** One of my questions as `/api/me/questions` sends it (no `eventName`: a question about days only). */
export type MyQuestion = {
  id: string;
  kind: 'event' | 'dates';
  eventId?: string;
  eventName?: string;
  canOpen: boolean;
  message: string;
  sentAt: string;
  days: MyQuestionDay[];
  answer: 'moge' | 'nie_moge' | 'pytanie' | 'czesciowo' | null;
  /** Owner 2026-10-30 01:30: what I answered, when a newer mark made `answer` say otherwise. */
  givenAnswer?: 'moge' | 'nie_moge' | 'pytanie' | 'czesciowo';
  answerNote: string;
  answeredAt: string | null;
  canAnswer: boolean;
  closed: boolean;
  stale: boolean;
  superseded: boolean;
  /** Owner 2026-10-30 21:31: my correspondence about it, oldest first; only the newest point may be re-clicked. */
  timeline?: TimelinePoint[];
  latestPointId?: string;
  /** Owner 2026-10-30 21:49: still in „Zapytania” (false: only in „Historia”). */
  active?: boolean;
  /** Only in „Historia”: the event was deleted. */
  eventDeleted?: true;
};
/**
 * Owner 2026-10-29 00:55: an event I answered „Tak” about whose crew I am not in yet (`/api/me/questions` → `awaiting`,
 * mine only, rule 7a): shown on those days as „Tak — czeka na przydział”; no `eventId` — the name is hidden from me.
 */
export type Awaiting = { questionId: string; eventId?: string; eventName: string; canOpen: boolean; days: string[] };
/** The event in the main calendar (its card opens there) — where every event name of „Mój kalendarz” leads (owner 00:55). */
export const eventUrl = (id: string) => `/wydarzenia?wydarzenie=${id}`;
/** The words of an awaiting event's status (muted, after its name). */
export const AWAITING_WORD = 'Tak — czeka na przydział';
export const MY_QUESTIONS_KEY = ['crew', 'me', 'questions'] as const;
export const MY_MARKS_KEY = ['crew', 'me', 'availability'] as const;
/**
 * Review M3: my entries of `/api/me/calendar` for a range — the calendar (whole weeks) and „Lista” (the month) ask
 * different ranges that may start on the same day, so the key holds both ends.
 */
export const myEntriesKey = (range: { from: string; to: string }) => [...CREW_KEY, 'me', range.from, range.to] as const;

export type Marker = '?' | 'Tak' | 'Nie';
/** „Powtórz…”: co tydzień (the same weekday) / co miesiąc (the same day of the month). */
export type RepeatEvery = 'week' | 'month';

/** '2099-11-07' → '7 listopada 2099'. */
export const dayLabel = (day: string) => `${Number(day.slice(8, 10))} ${MONTHS_GENITIVE[Number(day.slice(5, 7)) - 1]} ${day.slice(0, 4)}`;
/** '2099-11-07' → 'czw 7.11'. */
export const dayShortLabel = (day: string) => `${WEEKDAYS_SHORT[weekdayIndex(day)]!.toLocaleLowerCase('pl-PL')} ${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}`;
const STAMP = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
/** An instant as „1.11.2099, 11:40” (Warsaw). */
export const stampText = (iso: string) => STAMP.format(new Date(iso));
export type DayCell = {
  day: string;
  inMonth: boolean;
  past: boolean;
  today: boolean;
  mark?: MyMark & { hours?: string };
  entries: MyAssignment[];
  /** Owner 00:55: events I said „Tak” about on this day, not in their crew yet. */
  awaiting: Awaiting[];
  question: Marker | null;
  /** Owner 2026-10-30 01:30: the marker's answer was changed by a newer mark — „zmieniono 30.10 01:35 — Ty”. */
  questionNote?: string;
  /** Owner 2026-11-03 23:41: my private tasks due this day (open ones only, as the date views of „Moje zadania”). */
  tasks: PrivateTask[];
};

/** The month of `anchor` in weeks from Monday, each day with my mark, my events, the question marker and my open private tasks. */
export function monthCells(anchor: string, today: string, marks: readonly MyMark[], entries: readonly MyAssignment[], questions: readonly MyQuestion[], awaiting: readonly Awaiting[] = [], tasks: readonly PrivateTask[] = []): DayCell[][] {
  const month = anchor.slice(0, 7);
  const open = tasks.filter((t) => !t.done);
  const byDay = new Map(marks.map((m) => [m.day, m]));
  return monthGrid(anchor).map((week) =>
    week.map((day) => {
      const mark = byDay.get(day);
      const question = questionMarker(day, today, questions);
      const change = question === 'Tak' || question === 'Nie' ? answeredOn(day, questions)?.changed : undefined;
      return {
        day,
        inMonth: day.slice(0, 7) === month,
        past: day < today,
        today: day === today,
        ...(mark ? { mark: { ...mark, ...(mark.startsAt && mark.endsAt ? { hours: hoursText(mark.startsAt, mark.endsAt) } : {}) } } : {}),
        entries: entries.filter((e) => e.days?.includes(day)),
        awaiting: awaiting.filter((a) => a.days.includes(day)),
        question,
        ...(change ? { questionNote: changeNote(change, true) } : {}),
        tasks: open.filter((t) => t.dueDay === day),
      };
    }),
  );
}

/** A private task as the month and the day's panel say it: „10:00 Zadzwonić” (the time only when it has one). */
export const taskText = (t: Pick<PrivateTask, 'title' | 'dueTime'>) => (t.dueTime ? `${t.dueTime} ${t.title}` : t.title);

/** The event's name, or „Zapytanie o dni” for a question about days only. */
export const questionTitle = (q: Pick<MyQuestion, 'eventName'>) => q.eventName ?? 'Zapytanie o dni';

/** The questions about `day` (every one, also those that cannot be answered any more). */
export const questionsOn = (day: string, questions: readonly MyQuestion[]) => questions.filter((q) => q.days.some((d) => d.day === day));

/** Whether my answer on `day` can be given or changed now: the question still open, the day not past, not stale. */
export function dayAnswerable(q: MyQuestion, day: string, today: string): boolean {
  const d = q.days.find((x) => x.day === day);
  return !!d && q.canAnswer && !d.stale && day >= today;
}

/**
 * The marker of a day: „?” while (today or later) a question about it waits for my answer (and can get one), else my latest answer on
 * it („Tak” / „Nie”); a replaced question or one never answered and closed — nothing.
 */
export function questionMarker(day: string, today: string, questions: readonly MyQuestion[]): Marker | null {
  const live = questionsOn(day, questions).filter((q) => !q.superseded);
  const answerOf = (q: MyQuestion) => q.days.find((d) => d.day === day)?.answer ?? null;
  // review M4: a day that has passed cannot be answered any more — no „?” on it
  if (day >= today && live.some((q) => q.canAnswer && answerOf(q) === null)) return '?';
  const latest = answeredOn(day, questions);
  if (!latest) return null;
  return latest.answer ? 'Tak' : 'Nie';
}

/** My latest answer on `day` (as it stands: owner 2026-10-30 01:30) among the questions not replaced; none — undefined. */
function answeredOn(day: string, questions: readonly MyQuestion[]): MyQuestionDay | undefined {
  const answered = questionsOn(day, questions)
    .filter((q) => !q.superseded && (q.days.find((d) => d.day === day)?.answer ?? null) !== null)
    .sort((a, b) => b.sentAt.localeCompare(a.sentAt));
  return answered[0]?.days.find((d) => d.day === day);
}

/** „Mój kalendarz (N)”: my questions without an answer that can still get one. */
export const waitingCount = (questions: readonly MyQuestion[]) => questions.filter((q) => q.answer === null && q.canAnswer).length;

/** My status in an event as the side panel (#209) says it: a dot's tone and a word. */
export function statusTone(status: AssignmentStatus): { tone: 'accent' | 'success' | 'danger'; word: string } {
  return { tone: status === 'potwierdzony' ? 'success' : status === 'odmowa' ? 'danger' : 'accent', word: ASSIGNMENT_STATUSES[status] };
}

/** The month grid's selection: days picked with Cmd / Alt (never past ones) and the day whose panel is open. */
export type Selection = { selected: string[]; open: string | null };

/**
 * A click on a day: plain — that day's panel (the selection stays, as in the grid, #208), or, on the day whose panel is
 * open, the panel closes (owner 2026-10-29 00:21); with Cmd / Alt — the day joins the selection (or leaves it), which
 * starts from the day whose panel was open, as the grid's Cmd-click does (#208).
 */
export function selectionAfterClick(s: Selection, day: string, add: boolean, today: string): Selection {
  if (!add) return { selected: s.selected, open: s.open === day ? null : day };
  if (day < today) return s;
  const base = s.selected.length > 0 ? s.selected : s.open && s.open >= today ? [s.open] : [];
  const selected = base.includes(day) ? base.filter((d) => d !== day) : [...base, day].sort();
  return { selected, open: null };
}

/**
 * The days the open panel works on (owner 2026-10-29 00:21: „zaznaczenie kilku dat i po kliknięciu w jedno z nich
 * zmienia parametry … we wszystkich zaznaczonych”): every selected day when the open day is one of several selected,
 * else the open day alone; no panel — none.
 */
export function panelDays(s: Selection): string[] {
  if (!s.open) return [];
  return s.selected.length > 1 && s.selected.includes(s.open) ? s.selected : [s.open];
}

/** The day's panel docked to the right edge (owner 2026-10-29 00:53): its width and the gap it keeps from the month. */
export const PANEL_WIDTH = 380;
export const PANEL_GAP = 16;
/**
 * How far the page scrolls sideways when the docked panel opens (owner 00:53: „nie zwężaj kalendarza”): just enough for
 * the month's right edge (+ the gap) to end before the panel; none when it already does — and never so far that the
 * month's left edge leaves the window (a window too narrow for both: the panel covers the month's right edge).
 */
export function panelShift(month: { left: number; right: number }, viewport: number, panel = PANEL_WIDTH, gap = PANEL_GAP): number {
  return Math.max(0, Math.min(Math.ceil(month.right + gap - (viewport - panel)), Math.floor(month.left)));
}
